/**
 * "The collection changed" — one signal, every count that is on screen.
 *
 * **What this fixes.** Setup's Decks and Tags panels load once at mount and never again, so running
 * Frequency sort, Audio or Priority cleanup left every deck and tag count on screen asserting the
 * numbers from before the run — until you navigated away and back, which unmounts Setup and makes
 * it look like it had been fine all along. The rail's due badge and Read's counters refresh on
 * focus, which is no help either: a job fires at 03:00 with nobody watching, and a tab left open
 * overnight is exactly the case with no focus event to hang a refresh on.
 *
 * **Every writer, not just jobs.** It began as "a job finished", which left the same staleness in
 * place for every other writer — an MCP `add_note`, a `/bulk/*` apply, deck and tag admin. The
 * server now publishes `collectionChanged` from `AnkiCollection.write` itself, so one signal covers
 * all of them; `App` is still the only bridge from the stream to this module, and it is where a
 * tab's own writes are filtered out.
 *
 * **Not a timer.** `refresh.ts` is an explicit commitment against polling and this keeps it — the
 * signal is a fact that already reaches the browser over SSE, and with no job running this costs
 * nothing at all.
 *
 * **A `Set`, not a library.** `web/` depends on `react` and `react-dom` and nothing else, and this
 * is the whole of a one-topic pub-sub.
 *
 * **Blunt on purpose.** The frame says who wrote and nothing about what they touched, so it means
 * "ask again" rather than "ask about tags". The precise version — a `touches` declaration widened
 * into the payload — would save a couple of GETs a few times a day, at the cost of a vocabulary
 * crossing the language boundary with a new way to be wrong that **fails silent**: an understated
 * `touches` reproduces exactly the staleness this module exists for, with nothing red anywhere.
 * The argument only got stronger when the publish moved to `AnkiCollection.write`, which sees an
 * opaque closure and genuinely cannot know what it changed. Revisit only if the reads get
 * expensive.
 */

const listeners = new Set<() => void>();

/** Subscribe. Returns the unsubscribe, so it drops straight out of a `useEffect`. */
export function onCollectionChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Tell everyone their numbers are stale.
 *
 * Each listener is isolated: one that throws must not stop the rest from ever hearing again, which
 * is what a bare `forEach` over a shared set would do the first time a panel had a bad render.
 */
export function notifyCollectionChanged(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A count that failed to refresh is not worth taking the others down for.
    }
  }
}
